import { supabase } from '@/lib/supabase'
import type {
  Devis,
  DevisFormData,
  DevisFilters,
  DevisStats,
  DevisStatus,
  SignatureData,
  LigneDevis
} from '@/types/devis'
import { generateQuotePDF } from './pdf'
import { emailService } from './email'
import { createDevisSignatureEmail, createDevisEnvoiEmail } from '@/utils/emailTemplates'
import { savePdfToProjectDocuments } from './projectDocuments'
import { qontoApiCall, getQontoStatus, downloadQontoAttachment } from './qontoApi'

// Helper pour generer un token unique (crypto-secure)
function generateToken(): string {
  const bytes = new Uint8Array(24)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

// Helper pour calculer les totaux
export function calculateTotalHT(lignes: LigneDevis[]): number {
  return lignes.reduce((sum, ligne) => sum + ligne.total_ht, 0)
}

export function calculateTVA(lignes: LigneDevis[], tauxTva: number = 20): number {
  const totalHT = calculateTotalHT(lignes)
  return totalHT * (tauxTva / 100)
}

export function calculateTotalTTC(lignes: LigneDevis[], tauxTva: number = 20): number {
  const totalHT = calculateTotalHT(lignes)
  return totalHT * (1 + tauxTva / 100)
}

// Note: Les types sont forces avec 'as any' car le schema de la base de donnees
// doit etre mis a jour avec les nouveaux champs (titre, description, share_token, etc.)
// Une fois la migration effectuee, regenerez les types avec: npx supabase gen types

export const devisService = {
  async getAll(filters?: DevisFilters): Promise<Devis[]> {
    let query = supabase
      .from('devis')
      .select(`
        *,
        contact:users!contact_id(id, full_name, email, company)
      `)
      .order('created_at', { ascending: false })

    if (filters?.search) {
      query = query.or(`numero.ilike.%${filters.search}%,titre.ilike.%${filters.search}%`)
    }

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }

    if (filters?.contactId) {
      query = query.eq('contact_id', filters.contactId)
    }

    if (filters?.dateFrom) {
      query = query.gte('date_emission', filters.dateFrom)
    }

    if (filters?.dateTo) {
      query = query.lte('date_emission', filters.dateTo)
    }

    const { data, error } = await query

    if (error) throw error
    return (data || []) as Devis[]
  },

  async getById(id: string): Promise<Devis | null> {
    const { data, error } = await supabase
      .from('devis')
      .select(`
        *,
        contact:users!contact_id(id, full_name, email, company, phone, address)
      `)
      .eq('id', id)
      .single()

    if (error) throw error
    return data as Devis
  },

  async getByToken(token: string): Promise<Devis | null> {
    const { data, error } = await supabase
      .from('devis')
      .select(`
        *,
        contact:users!contact_id(id, full_name, email, company, phone, address)
      `)
      .eq('share_token', token)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null // Not found
      throw error
    }
    return data as Devis
  },

  async create(devis: DevisFormData): Promise<Devis> {
    const shareToken = generateToken()

    const insertData = {
      numero: devis.numero,
      contact_id: devis.contact_id,
      projet_id: devis.projet_id || null,
      titre: devis.titre,
      description: devis.description,
      date_emission: devis.date_emission,
      date_validite: devis.date_validite,
      validite_jours: devis.validite_jours || 30,
      status: devis.status || 'draft',
      lignes: devis.lignes,
      montant_ht: devis.montant_ht || 0,
      taux_tva: devis.taux_tva,
      montant_tva: devis.montant_tva || 0,
      montant_ttc: devis.montant_ttc || 0,
      conditions: devis.conditions,
      notes_internes: devis.notes_internes,
      share_token: shareToken,
    }

    const { data, error } = await (supabase
      .from('devis')
      .insert(insertData as never)
      .select(`
        *,
        contact:users!contact_id(id, full_name, email, company)
      `)
      .single())

    if (error) throw error

    const createdDevis = data as Devis

    // Sync avec Qonto (non bloquant)
    try {
      const status = await getQontoStatus()
      if (status.connected) {
        const contact = createdDevis.contact

        // Trouver ou créer le client Qonto
        let qontoClientId: string | null = null

        if (devis.contact_id) {
          // Vérifier si le client a déjà un qonto_client_id
          const { data: userData } = await supabase
            .from('users')
            .select('qonto_client_id, full_name, email, company')
            .eq('id', devis.contact_id)
            .single()

          if (userData?.qonto_client_id) {
            qontoClientId = userData.qonto_client_id
          } else if (userData) {
            // Créer le client sur Qonto
            try {
              const isCompany = !!userData.company
              const clientBody = isCompany
                ? { name: userData.company, email: userData.email, type: 'company' }
                : { first_name: (userData.full_name || 'Client').split(' ')[0], last_name: (userData.full_name || '').split(' ').slice(1).join(' ') || '-', email: userData.email, type: 'individual' }
              const result = await qontoApiCall<{ client: { id: string } }>('/clients', {
                method: 'POST',
                body: clientBody,
              })
              if (result?.client?.id) {
                qontoClientId = result.client.id
                await supabase.from('users').update({ qonto_client_id: result.client.id }).eq('id', devis.contact_id)
              }
            } catch (clientErr) {
              console.error('Erreur création client Qonto:', clientErr)
            }
          }
        }

        if (qontoClientId) {
          // Assurer que le client a locale/currency/billing_address
          await qontoApiCall(`/clients/${qontoClientId}`, {
            method: 'PATCH',
            body: { locale: 'fr', currency: 'EUR', billing_address: { street_address: '-', city: '-', zip_code: '00000', country_code: 'FR' } },
          }).catch(() => {})

          const tvaDecimal = (devis.taux_tva ?? 20) / 100

          const qontoQuote = await qontoApiCall<{ quote: { id: string; quote_url: string } }>('/quotes', {
            method: 'POST',
            body: {
              client_id: qontoClientId,
              issue_date: devis.date_emission,
              expiry_date: devis.date_validite,
              currency: 'EUR',
              terms_and_conditions: devis.conditions || '',
              items: (devis.lignes || []).map((l: LigneDevis) => ({
                title: (l.description || 'Article').substring(0, 120),
                quantity: String(l.quantite || 1),
                unit_price: { value: String(l.prix_unitaire || 0), currency: 'EUR' },
                vat_rate: String(tvaDecimal),
                currency: 'EUR',
              })),
            },
          })

          if (qontoQuote?.quote) {
            await supabase
              .from('devis')
              .update({
                qonto_id: qontoQuote.quote.id,
                qonto_url: qontoQuote.quote.quote_url,
              } as never)
              .eq('id', createdDevis.id)

            createdDevis.qonto_id = qontoQuote.quote.id
            createdDevis.qonto_url = qontoQuote.quote.quote_url

            // Sauvegarder le PDF Qonto dans les documents projet
            // Le PDF est généré de manière asynchrone par Qonto, on retry si attachment_id est null
            if (devis.projet_id) {
              try {
                let attachmentId: string | undefined
                for (let attempt = 0; attempt < 3; attempt++) {
                  const quoteDetail = await qontoApiCall<{ quote: { attachment_id?: string } }>(`/quotes/${qontoQuote.quote.id}`)
                  attachmentId = quoteDetail?.quote?.attachment_id || undefined
                  if (attachmentId) break
                  await new Promise(resolve => setTimeout(resolve, 2000))
                }
                if (attachmentId) {
                  const pdfBlob = await downloadQontoAttachment(attachmentId)
                  await savePdfToProjectDocuments(
                    devis.projet_id,
                    pdfBlob,
                    `Devis ${createdDevis.numero} - Qonto`,
                    'contrat',
                    `PDF Qonto du devis ${createdDevis.numero}`
                  )
                }
              } catch (storageErr) {
                console.error('Erreur sauvegarde PDF Qonto devis:', storageErr)
              }
            }
          }
        }
      }
    } catch (qontoError) {
      console.error('Erreur sync Qonto devis:', qontoError)
    }

    // Auto-ajout du PDF dans les documents projet (seulement si pas deja sauve via Qonto)
    if (devis.projet_id && !createdDevis.qonto_id) {
      try {
        const fullDevis = await this.getById(createdDevis.id)
        if (fullDevis) {
          const pdfBlob = await generateQuotePDF(fullDevis)
          await savePdfToProjectDocuments(
            devis.projet_id,
            pdfBlob,
            `Devis_${createdDevis.numero}.pdf`,
            'contrat',
            `Devis ${createdDevis.numero} - ${createdDevis.titre || 'Sans titre'}`
          )
        }
      } catch (pdfError) {
        console.error('Erreur auto-ajout PDF devis dans documents projet:', pdfError)
      }
    }

    return createdDevis
  },

  async update(id: string, devis: Partial<DevisFormData>): Promise<Devis> {
    const updateData: Record<string, unknown> = {
      ...devis,
      updated_at: new Date().toISOString(),
    }

    if (devis.montant_ht !== undefined) {
      updateData.montant_ht = devis.montant_ht || 0
    }
    if (devis.montant_tva !== undefined) {
      updateData.montant_tva = devis.montant_tva || 0
    }
    if (devis.montant_ttc !== undefined) {
      updateData.montant_ttc = devis.montant_ttc || 0
    }

    const { data, error } = await (supabase
      .from('devis')
      .update(updateData as never)
      .eq('id', id)
      .select(`
        *,
        contact:users!contact_id(id, full_name, email, company)
      `)
      .single())

    if (error) throw error
    return data as Devis
  },

  async updateStatus(id: string, status: DevisStatus): Promise<Devis> {
    const updateData: Record<string, unknown> = {
      status,
      updated_at: new Date().toISOString(),
    }

    // Ajouter la date de reponse pour accepte/refuse
    if (status === 'accepted' || status === 'rejected') {
      updateData.date_reponse = new Date().toISOString()
    }

    const { data, error } = await (supabase
      .from('devis')
      .update(updateData as never)
      .eq('id', id)
      .select(`
        *,
        contact:users!contact_id(id, full_name, email, company)
      `)
      .single())

    if (error) throw error
    return data as Devis
  },

  /**
   * Refuse un devis avec une raison et génère automatiquement une demande d'intervention
   */
  async rejectDevis(id: string, rejectionReason: string): Promise<{ devis: Devis; interventionId: string }> {
    // Récupérer le devis pour avoir les infos du client
    const devis = await this.getById(id)
    if (!devis) {
      throw new Error('Devis non trouvé')
    }

    // Mettre à jour le statut du devis
    const updateData = {
      status: 'rejected' as DevisStatus,
      date_reponse: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    const { data: updatedDevis, error: devisError } = await supabase
      .from('devis')
      .update(updateData as never)
      .eq('id', id)
      .select(`
        *,
        contact:users!contact_id(id, full_name, email, company)
      `)
      .single()

    if (devisError) throw devisError

    // Créer une demande d'intervention automatique
    const interventionData = {
      user_id: devis.client_id,
      request_type: 'devis_declined',
      title: `Devis ${devis.numero} refusé`,
      description: `Le client a refusé le devis ${devis.numero} pour la raison suivante :\n\n${rejectionReason}\n\nMontant du devis : ${devis.montant_ttc.toFixed(2)}€ TTC\nTitre : ${devis.titre || 'N/A'}`,
      priority: 'high',
      status: 'pending',
      metadata: {
        devis_id: devis.id,
        devis_numero: devis.numero,
        rejection_reason: rejectionReason,
        devis_montant: devis.montant_ttc,
      },
    }

    const { data: intervention, error: interventionError } = await supabase
      .from('client_requests')
      .insert(interventionData as never)
      .select('id')
      .single()

    if (interventionError) {
      console.error('Erreur création intervention:', interventionError)
      // On ne fait pas échouer la requête si l'intervention ne se crée pas
    }

    return {
      devis: updatedDevis as Devis,
      interventionId: intervention?.id || '',
    }
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase.from('devis').delete().eq('id', id)

    if (error) throw error
  },

  async generateNumero(): Promise<string> {
    const year = new Date().getFullYear()
    const { count } = await supabase
      .from('devis')
      .select('*', { count: 'exact', head: true })
      .gte('date_emission', `${year}-01-01`)

    const nextNumber = (count ?? 0) + 1
    return `DEV-${year}-${nextNumber.toString().padStart(4, '0')}`
  },

  // STORY-018: Tracking de l'ouverture
  async trackView(token: string): Promise<void> {
    const devis = await this.getByToken(token)

    if (devis && !devis.date_vue) {
      const updateData = {
        status: 'viewed',
        date_vue: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }

      await (supabase
        .from('devis')
        .update(updateData as never)
        .eq('id', devis.id))

      // TODO: Creer une notification pour l'admin
    }
  },

  // STORY-020: Signature du devis
  async signDevis(token: string, signatureData: SignatureData): Promise<Devis> {
    const devis = await this.getByToken(token)

    if (!devis) {
      throw new Error('Devis non trouve')
    }

    if (new Date(devis.date_validite) < new Date()) {
      throw new Error('Ce devis a expire')
    }

    const updateData = {
      status: 'accepted',
      signature_client: signatureData.signature,
      signature_date: signatureData.timestamp,
      ip_signature: signatureData.ip,
      date_reponse: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    const { data, error } = await (supabase
      .from('devis')
      .update(updateData as never)
      .eq('id', devis.id)
      .select(`
        *,
        contact:users!contact_id(id, full_name, email, company)
      `)
      .single())

    if (error) throw error

    const acceptedDevis = data as Devis

    // Générer et sauvegarder le PDF du devis signé
    try {
      await this.saveAcceptedQuotePDF(acceptedDevis.id)
    } catch (pdfError) {
      console.error('Erreur lors de la génération du PDF:', pdfError)
      // Ne pas bloquer l'acceptation si la génération du PDF échoue
      // Le PDF peut être régénéré plus tard si nécessaire
    }

    // Envoyer email de confirmation au client
    try {
      const clientEmail = acceptedDevis.contact?.email
      const clientName = acceptedDevis.contact
        ? `${acceptedDevis.contact.prenom} ${acceptedDevis.contact.nom}`
        : 'Client'

      if (clientEmail) {
        const htmlContent = createDevisSignatureEmail({
          clientName,
          devisNumero: acceptedDevis.numero,
          devisTitre: acceptedDevis.titre || 'N/A',
          montantTTC: acceptedDevis.montant_ttc,
        })

        const emailRequest = {
          to: clientEmail,
          toName: clientName,
          subject: `Confirmation de signature - Devis ${acceptedDevis.numero}`,
          html: htmlContent,
          text: `Merci pour votre signature ! Nous confirmons la signature du devis ${acceptedDevis.numero}. Montant: ${acceptedDevis.montant_ttc.toFixed(2)}€ TTC.`,
          referenceType: 'devis' as const,
          referenceId: acceptedDevis.id,
        }

        // Envoyer à tous les membres du projet si projet_id existe
        if (acceptedDevis.projet_id) {
          await emailService.sendEmailToProject(acceptedDevis.projet_id, emailRequest)
        } else {
          await emailService.sendEmail(emailRequest)
        }
      }
    } catch (emailError) {
      console.error('Erreur lors de l\'envoi de l\'email de confirmation:', emailError)
      // Ne pas bloquer l'acceptation si l'email échoue
    }

    return acceptedDevis
  },

  // STORY-018: Refuser le devis (côté client, par token)
  async rejectDevisByToken(token: string, message?: string): Promise<Devis> {
    const devis = await this.getByToken(token)

    if (!devis) {
      throw new Error('Devis non trouve')
    }

    const updateData = {
      status: 'rejected',
      message_client: message,
      date_reponse: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    const { data, error } = await (supabase
      .from('devis')
      .update(updateData as never)
      .eq('id', devis.id)
      .select(`
        *,
        contact:users!contact_id(id, full_name, email, company)
      `)
      .single())

    if (error) throw error

    // TODO: Notifier l'admin

    return data as Devis
  },

  // STORY-018: Convertir en facture
  async convertToFacture(devisId: string): Promise<{ factureId: string }> {
    const devis = await this.getById(devisId)

    if (!devis) {
      throw new Error('Devis non trouve')
    }

    if (devis.status !== 'accepted') {
      throw new Error('Seuls les devis acceptes peuvent etre convertis en facture')
    }

    // Generer le numero de facture
    const year = new Date().getFullYear()
    const { count } = await supabase
      .from('facture')
      .select('*', { count: 'exact', head: true })
      .gte('date_creation', `${year}-01-01`)

    const numeroFacture = `FAC-${year}-${((count ?? 0) + 1).toString().padStart(4, '0')}`

    // Preparer les dates
    const dateCreation = new Date().toISOString().split('T')[0]
    const dateEcheance = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

    // Creer la facture (using the real facture table)
    const factureData = {
      numero: numeroFacture,
      utilisateur_id: devis.contact_id,
      projet_id: devis.projet_id || null,
      date_creation: dateCreation,
      date_echeance: dateEcheance,
      produits_json: devis.lignes,
      total_ht: devis.montant_ht || 0,
      tva: devis.montant_tva || 0,
      total_ttc: devis.montant_ttc || 0,
      conditions: devis.conditions,
      notes_internes: devis.notes_internes,
      statut: 'draft',
      devis_id: devisId,
      montant: devis.montant_ttc || 0,
    }

    const { data: facture, error: factureError } = await supabase
      .from('facture')
      .insert(factureData)
      .select()
      .single()

    if (factureError) throw factureError

    // Mettre a jour le devis
    const updateData = {
      status: 'converted',
      facture_id: (facture as { id: string }).id,
      updated_at: new Date().toISOString(),
    }

    await (supabase
      .from('devis')
      .update(updateData as never)
      .eq('id', devisId))

    const factureId = (facture as { id: string }).id

    return { factureId }
  },

  // STORY-017: Envoyer le devis par email
  async sendDevis(devisId: string): Promise<Devis> {
    const updateData = {
      status: 'sent',
      updated_at: new Date().toISOString(),
    }

    const { data, error } = await (supabase
      .from('devis')
      .update(updateData as never)
      .eq('id', devisId)
      .select(`
        *,
        contact:users!contact_id(id, full_name, email, company)
      `)
      .single())

    if (error) throw error

    const devis = data as Devis

    // Envoyer l'email avec le lien vers le devis
    try {
      const clientEmail = devis.contact?.email
      const clientName = devis.contact?.full_name || devis.contact?.company || 'Client'

      if (clientEmail && devis.share_token) {
        const appUrl = import.meta.env.VITE_APP_URL || window.location.origin
        const devisUrl = `${appUrl}/devis/view/${devis.share_token}`

        const htmlContent = createDevisEnvoiEmail({
          clientName,
          devisNumero: devis.numero,
          devisTitre: devis.titre || 'N/A',
          montantTTC: devis.montant_ttc,
          dateValidite: devis.date_validite,
          devisUrl,
        })

        // Récupérer le PDF en PJ (Qonto si disponible, sinon PDF local)
        let attachments: { filename: string; content: string }[] | undefined
        if (devis.qonto_id) {
          try {
            const qontoQuote = await qontoApiCall<{ quote: { attachment_id: string } }>(`/quotes/${devis.qonto_id}`)
            const attachId = qontoQuote?.quote?.attachment_id
            if (attachId) {
              const pdfBlob = await downloadQontoAttachment(attachId)
              const buffer = await pdfBlob.arrayBuffer()
              const bytes = new Uint8Array(buffer)
              let binary = ''
              for (let i = 0; i < bytes.length; i++) {
                binary += String.fromCharCode(bytes[i])
              }
              const base64 = btoa(binary)
              attachments = [{
                filename: `Devis_${devis.numero}.pdf`,
                content: base64,
              }]
            }
          } catch (pdfErr) {
            console.error('Erreur récupération PDF Qonto pour email:', pdfErr)
          }
        }

        // Fallback: générer le PDF local si pas de PDF Qonto
        if (!attachments) {
          try {
            const pdfBlob = await generateQuotePDF(devis)
            const buffer = await pdfBlob.arrayBuffer()
            const bytes = new Uint8Array(buffer)
            let binary = ''
            for (let i = 0; i < bytes.length; i++) {
              binary += String.fromCharCode(bytes[i])
            }
            const base64 = btoa(binary)
            attachments = [{
              filename: `Devis_${devis.numero}.pdf`,
              content: base64,
            }]
          } catch (pdfErr) {
            console.error('Erreur génération PDF local pour email devis:', pdfErr)
          }
        }

        const emailRequest = {
          to: clientEmail,
          toName: clientName,
          subject: `Votre devis ${devis.numero} - ${devis.titre || 'LesSablesWeb'}`,
          html: htmlContent,
          text: `Votre devis ${devis.numero} est prêt ! Consultez-le en ligne : ${devisUrl}`,
          referenceType: 'devis' as const,
          referenceId: devis.id,
          attachments,
        }

        // Envoyer à tous les membres du projet si projet_id existe
        if (devis.projet_id) {
          await emailService.sendEmailToProject(devis.projet_id, emailRequest)
        } else {
          await emailService.sendEmail(emailRequest)
        }
      }
    } catch (emailError) {
      console.error('Erreur lors de l\'envoi de l\'email du devis:', emailError)
    }

    // Sauvegarder le PDF Qonto dans les documents projet (si pas déjà fait)
    if (devis.qonto_id && devis.projet_id) {
      try {
        const { data: existingDoc } = await supabase
          .from('project_documents')
          .select('id')
          .eq('project_id', devis.projet_id)
          .ilike('nom', `%${devis.numero}%Qonto%`)
          .maybeSingle()

        if (!existingDoc) {
          const quoteDetail = await qontoApiCall<{ quote: { attachment_id?: string } }>(`/quotes/${devis.qonto_id}`)
          const attachmentId = quoteDetail?.quote?.attachment_id
          if (attachmentId) {
            const pdfBlob = await downloadQontoAttachment(attachmentId)
            await savePdfToProjectDocuments(
              devis.projet_id,
              pdfBlob,
              `Devis ${devis.numero} - Qonto`,
              'contrat',
              `PDF Qonto du devis ${devis.numero}`
            )
          }
        }
      } catch (docErr) {
        console.error('Erreur sauvegarde PDF Qonto devis dans documents projet:', docErr)
      }
    }

    return devis
  },

  // STORY-016: Obtenir les statistiques
  async getStats(): Promise<DevisStats> {
    const { data: devisList, error } = await supabase
      .from('devis')
      .select('status, montant_ttc')

    if (error) throw error

    interface DevisStatRow {
      status: string
      montant_ttc: number
    }

    const devis = (devisList || []) as DevisStatRow[]

    const total = devis.length
    const enCours = devis.filter(d => ['sent', 'viewed'].includes(d.status)).length
    const acceptes = devis.filter(d => d.status === 'accepted' || d.status === 'converted').length
    const montantEnAttente = devis
      .filter(d => ['sent', 'viewed'].includes(d.status))
      .reduce((sum, d) => sum + (d.montant_ttc || 0), 0)

    return {
      total,
      en_cours: enCours,
      taux_conversion: total > 0 ? Math.round((acceptes / total) * 100) : 0,
      montant_en_attente: montantEnAttente,
    }
  },

  // Sauvegarder le PDF du devis accepte
  async saveAcceptedQuotePDF(devisId: string): Promise<void> {
    // Récupérer le devis complet avec les informations de contact
    const devis = await this.getById(devisId)

    if (!devis) {
      throw new Error('Devis non trouvé')
    }

    if (devis.status !== 'accepted') {
      throw new Error('Le devis doit être accepté pour générer le PDF')
    }

    // Générer le PDF
    const pdfBlob = await generateQuotePDF(devis)

    // Créer un nom de fichier unique
    const fileName = `devis/${devisId}/Devis_${devis.numero}_signe_${Date.now()}.pdf`

    // Uploader dans le bucket 'documents'
    const { error: uploadError } = await supabase.storage
      .from('documents')
      .upload(fileName, pdfBlob, {
        contentType: 'application/pdf',
        upsert: false
      })

    if (uploadError) {
      console.error('Erreur upload PDF:', uploadError)
      throw new Error(`Erreur lors de l'upload du PDF: ${uploadError.message}`)
    }

    // Créer l'enregistrement dans la table documents
    const documentData = {
      name: `Devis ${devis.numero} - Signé`,
      file_path: fileName,
      storage_bucket: 'documents',
      file_type: 'application/pdf',
      file_size: pdfBlob.size,
      category: 'quote',
      client_id: devis.contact_id,
      project_id: devis.projet_id || null,
      visible_to_client: true,
      metadata: {
        quote_id: devisId,
        quote_number: devis.numero,
        signed_date: devis.signature_date,
        generated_at: new Date().toISOString()
      }
    }

    const { error: insertError } = await supabase
      .from('documents')
      .insert(documentData)

    if (insertError) {
      console.error('Erreur création document:', insertError)
      // Nettoyer le fichier uploadé si l'insertion échoue
      await supabase.storage.from('documents').remove([fileName])
      throw new Error(`Erreur lors de la création du document: ${insertError.message}`)
    }
  },

  // Dupliquer un devis
  async duplicate(devisId: string): Promise<Devis> {
    const devis = await this.getById(devisId)

    if (!devis) {
      throw new Error('Devis non trouve')
    }

    const numero = await this.generateNumero()
    const shareToken = generateToken()
    const today = new Date()
    const validiteJours = devis.validite_jours || 30
    const dateValidite = new Date(today.getTime() + validiteJours * 24 * 60 * 60 * 1000)

    const insertData = {
      numero,
      contact_id: devis.contact_id,
      titre: (devis.titre || '') + ' (copie)',
      description: devis.description,
      date_emission: today.toISOString().split('T')[0],
      date_validite: dateValidite.toISOString().split('T')[0],
      validite_jours: validiteJours,
      status: 'draft',
      lignes: devis.lignes,
      montant_ht: devis.montant_ht,
      taux_tva: devis.taux_tva,
      montant_tva: devis.montant_tva,
      montant_ttc: devis.montant_ttc,
      conditions: devis.conditions,
      notes_internes: devis.notes_internes,
      share_token: shareToken,
    }

    const { data, error } = await (supabase
      .from('devis')
      .insert(insertData as never)
      .select(`
        *,
        contact:users!contact_id(id, full_name, email, company)
      `)
      .single())

    if (error) throw error
    return data as Devis
  },
}
